import AUTH from '@/constants/auth';
import * as mt from './mutation-types';
import EventBus from '@/event-bus';
import i18n from '@/i18n';
import {
  loginWithCredentials,
  logoutUsingToken,
  setAuthorizationHeader,
  unsetAuthorizationHeader,
  addResponseError401Interceptor,
  removeResponseError401Interceptor,
  integrationLogin,
} from '@/api/auth/index';

import { RootState } from '../types';
import { Auth } from './types';
import { ActionContext } from 'vuex';

const formatRetryAfter = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return i18n.t('login.error.retry_soon') as string;
  }
  if (seconds < 60) {
    return i18n.tc('login.error.retry_seconds', Math.ceil(seconds), { count: Math.ceil(seconds) }) as string;
  }
  const minutes = Math.ceil(seconds / 60);
  return i18n.tc('login.error.retry_minutes', minutes, { count: minutes }) as string;
};

/* The actions from vuex-store are responsible to:
  - Sign in using user using Credentials;
  - Logout User;
  - Save tokens in localStorage (TODO: create a service to manage storage)
  - After logging in emit `AUTH.LOGGED_IN_EVENT`
  - After logging out emit `AUTH.LOGGED_OUT_EVENT` */
export default {
  async [mt.AUTH_REQUEST]({ commit }: ActionContext<Auth, RootState>, credentials: { username: string; password: string; }) {
    commit(mt.AUTH_REQUEST);

    return loginWithCredentials(credentials)
      .then(response => response.data)
      .then((content) => {
        const accessToken = content.access_token;

        try {
          localStorage.setItem(AUTH.STORAGE_ACCESS_TOKEN, accessToken);
        } finally {
          setAuthorizationHeader(accessToken);
          addResponseError401Interceptor();

          commit(mt.AUTH_SUCCESS, { accessToken });
          EventBus.$emit(AUTH.LOGGED_IN_EVENT);
        }
        return content;
      }).catch((error) => {
        const status = error && error.response && error.response.status;
        const data = (error && error.response && error.response.data) || {};

        let errorType = 'auth.error';
        let errorMessage = i18n.t('login.error.invalid_credentials') as string;

        if (status === 429 || data.error === 'rate_limit_exceeded') {
          errorType = 'auth.rate_limit';
          errorMessage = i18n.t('login.error.rate_limit', {
            retry: formatRetryAfter(Number(data.retryAfterSeconds)),
          }) as string;
        }

        commit(mt.AUTH_ERROR, {
          type: errorType,
          message: errorMessage,
        });

        EventBus.$emit(AUTH.LOGIN_ERROR_EVENT);
        localStorage.removeItem(AUTH.STORAGE_ACCESS_TOKEN);

        throw error;
      });
  },
  
  [mt.AUTH_LOGOUT]( { commit, state } : ActionContext<Auth, RootState>, redirect?: string) {
    if (state.status !== 'logging-out') {
      commit(mt.AUTH_LOGGING_OUT);

      logoutUsingToken(state.accessToken).then(() => {
        commit(mt.AUTH_LOGOUT);
  
        localStorage.removeItem(AUTH.STORAGE_ACCESS_TOKEN);
  
        unsetAuthorizationHeader();
        removeResponseError401Interceptor();
  
        EventBus.$emit(AUTH.LOGGED_OUT_EVENT, redirect);
      });
    }
  },

  [mt.INTEGRATION_AUTH_REQUEST]( { commit } : ActionContext<Auth, RootState>, credentials: { token: string; }) {
    sessionStorage.setItem('integration', 'true');
    commit(mt.INTEGRATION_AUTH_REQUEST);
    
    const accessToken = credentials.token;

    try {
      localStorage.setItem(AUTH.STORAGE_ACCESS_TOKEN, accessToken);
    } finally {
      setAuthorizationHeader(accessToken);
      addResponseError401Interceptor();

      commit(mt.AUTH_SUCCESS, { accessToken });
      EventBus.$emit(AUTH.LOGGED_IN_EVENT);
    }
    // return content
  },
};
